<template>
  <BaseTemplate app-name="Gestão de Locais" page-title="">
    <template #content>
      <!-- Verificação de permissão -->
      <div v-if="!temPermissao" class="container mx-auto p-6">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <div class="mb-4">
            <svg class="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">Acesso Negado</h2>
          <p class="text-gray-600 dark:text-gray-300 mb-6">
            Você não tem permissão para acessar esta página. Apenas usuários com permissões de administrador podem gerenciar locais.
          </p>
          <button @click="voltar" class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg">
            Voltar
          </button>
        </div>
      </div>

      <div v-else class="container mx-auto p-6">
        <!-- Botão Voltar -->
        <button @click="voltar"
          class="hidden md:inline-flex mb-5 items-center gap-2 bg-blue-500 dark:bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors dark:hover:bg-blue-700">
          <SquareArrowLeft class="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <!-- Cabeçalho com títulos e botões -->
        <div class="flex flex-col sm:flex-row justify-between items-center mb-6">
          <h1 class="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <MapPin class="w-6 h-6 text-blue-500" />
            Gestão de Locais
          </h1>
          <div class="flex flex-wrap items-center gap-3 mt-4 sm:mt-0">
            <!-- Botão "Novo Local" -->
            <button @click="openModal()"
              class="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow">
              <PlusIcon class="w-5 h-5" />
              Novo Local
            </button>
          </div>
        </div>

        <!-- Filtro rápido (busca por nome) -->
        <div class="flex flex-col sm:flex-row gap-4 mb-6">
          <input v-model="filter" type="text" placeholder="Buscar por nome..."
            class="w-full sm:w-1/2 border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500" />
          <div class="flex items-center gap-2">
            <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input v-model="showInactive" type="checkbox" class="rounded" />
              Mostrar inativos
            </label>
          </div>
        </div>

        <!-- Mensagem de erro -->
        <p v-if="error" class="text-red-500 text-center mb-4">{{ error }}</p>

        <!-- Tabela desktop -->
        <div
          class="hidden sm:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto dark:text-white text-gray-800">
          <table class="w-full">
            <thead
              class="bg-blue-500 dark:bg-blue-600 text-white dark:text-gray-300 text-sm uppercase tracking-wide rounded-t-lg">
              <tr>
                <th @click="ordenarPor('nome')" class="cursor-pointer px-4 py-3 text-left">
                  Nome <span v-if="colunaOrdenada === 'nome'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th class="px-4 py-3 text-left">Descrição</th>
                <th @click="ordenarPor('ativo')" class="cursor-pointer px-4 py-3 text-left">
                  Status <span v-if="colunaOrdenada === 'ativo'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th class="px-4 py-3 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="local in locaisOrdenados" :key="local.id"
                class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                <td class="px-4 py-3 font-medium">{{ local.nome }}</td>
                <td class="px-4 py-3">{{ local.descricao || '-' }}</td>
                <td class="px-4 py-3">
                  <span :class="local.ativo ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'"
                    class="px-2 py-1 rounded-full text-xs font-semibold">
                    {{ local.ativo ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div class="flex justify-center gap-2">
                    <button @click="editLocal(local)"
                      class="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 rounded">
                      <Edit2Icon class="w-4 h-4" />
                    </button>
                    <button @click="deleteLocal(local)"
                      class="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded">
                      <Trash2Icon class="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Cards mobile -->
        <div class="sm:hidden space-y-4">
          <div v-for="local in locaisOrdenados" :key="local.id"
            class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="flex justify-between items-start mb-2">
              <h3 class="font-semibold text-gray-900 dark:text-white">{{ local.nome }}</h3>
              <span :class="local.ativo ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'"
                class="px-2 py-1 rounded-full text-xs font-semibold">
                {{ local.ativo ? 'Ativo' : 'Inativo' }}
              </span>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">{{ local.descricao || 'Sem descrição' }}</p>
            <div class="flex gap-2">
              <button @click="editLocal(local)"
                class="flex-1 flex items-center justify-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded">
                <Edit2Icon class="w-4 h-4" />
                Editar
              </button>
              <button @click="deleteLocal(local)"
                class="flex-1 flex items-center justify-center gap-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded">
                <Trash2Icon class="w-4 h-4" />
                Excluir
              </button>
            </div>
          </div>
        </div>

        <!-- Mensagem quando não há locais -->
        <div v-if="locaisFiltrados.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>Nenhum local encontrado.</p>
        </div>

        <!-- Modal de criação/edição -->
        <div v-if="isModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          @click.self="closeModal">
          <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md animate-fade-in">
            <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <MapPin class="w-5 h-5 text-blue-500" />
              {{ isEditMode ? "Editar Local" : "Novo Local" }}
            </h2>

            <!-- Campo Nome -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome <span class="text-red-500">*</span></label>
            <input v-model="currentLocal.nome" type="text" required
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3"
              placeholder="Ex: Sala 1, UTI, Enfermaria..." />

            <!-- Campo Descrição -->
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
            <textarea v-model="currentLocal.descricao" rows="3"
              class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3"
              placeholder="Descrição opcional do local..."></textarea>

            <!-- Campo Ativo -->
            <label class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              <input v-model="currentLocal.ativo" type="checkbox" class="rounded" />
              Local ativo
            </label>

            <!-- Botões Salvar / Cancelar -->
            <div class="flex justify-end space-x-3">
              <button @click="closeModal"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">
                <XIcon class="w-4 h-4" /> Cancelar
              </button>
              <button @click="saveLocal"
                class="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg">
                <SaveIcon class="w-4 h-4" />
                <span>Salvar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </template>
  </BaseTemplate>
</template>

<script setup>
import apiClient from '@/services/authService';
import BaseTemplate from '../templates/BaseTemplate.vue';
import { ref, computed, onMounted } from 'vue';
import {
  MapPin,
  PlusIcon,
  Edit2Icon,
  Trash2Icon,
  XIcon,
  SaveIcon,
  SquareArrowLeft
} from 'lucide-vue-next';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';

const router = useRouter();
const userStore = useUserStore();
const userGroups = computed(() => userStore.grupos || []);

// Verificar permissões
const gruposPermitidos = ['superadmin', 'desenvolvedor', 'nep'];
const temPermissao = computed(() => {
  return gruposPermitidos.some(grupo => userGroups.value.includes(grupo));
});

const voltar = () => router.go(-1);

const locais = ref([]);
const filter = ref('');
const error = ref(null);
const isModalOpen = ref(false);
const isEditMode = ref(false);
const showInactive = ref(false);

const currentLocal = ref({
  id: null,
  nome: '',
  descricao: '',
  ativo: true
});

// Ordenação
const colunaOrdenada = ref('nome');
const ordemAscendente = ref(true);

// Filtragem reativa de locais
const locaisFiltrados = computed(() =>
  locais.value.filter(local => {
    const nomeMatch = local.nome.toLowerCase().includes(filter.value.toLowerCase());
    const ativoMatch = showInactive.value || local.ativo;
    return nomeMatch && ativoMatch;
  })
);

// Computed para ordenar
const locaisOrdenados = computed(() => {
  const arr = locaisFiltrados.value.slice();
  arr.sort((a, b) => {
    let aVal = a[colunaOrdenada.value];
    let bVal = b[colunaOrdenada.value];
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (aVal < bVal) return ordemAscendente.value ? -1 : 1;
    if (aVal > bVal) return ordemAscendente.value ? 1 : -1;
    return 0;
  });
  return arr;
});

function ordenarPor(coluna) {
  if (colunaOrdenada.value === coluna) {
    ordemAscendente.value = !ordemAscendente.value;
  } else {
    colunaOrdenada.value = coluna;
    ordemAscendente.value = true;
  }
}

async function loadLocais() {
  try {
    error.value = null;
    const endpoint = showInactive.value ? '/api/v1/eventos-adversos/locais/todos/' : '/api/v1/eventos-adversos/locais/';
    const response = await apiClient.get(endpoint);
    locais.value = response.data;
  } catch (err) {
    error.value = 'Erro ao carregar locais.';
    console.error('Erro ao carregar locais:', err);
  }
}

function openModal(local = null) {
  if (local) {
    isEditMode.value = true;
    currentLocal.value = {
      id: local.id,
      nome: local.nome,
      descricao: local.descricao || '',
      ativo: local.ativo
    };
  } else {
    isEditMode.value = false;
    currentLocal.value = {
      id: null,
      nome: '',
      descricao: '',
      ativo: true
    };
  }
  isModalOpen.value = true;
}

function closeModal() {
  isModalOpen.value = false;
  currentLocal.value = {
    id: null,
    nome: '',
    descricao: '',
    ativo: true
  };
}

async function saveLocal() {
  if (!currentLocal.value.nome.trim()) {
    error.value = 'O nome do local é obrigatório.';
    return;
  }

  try {
    error.value = null;
    if (isEditMode.value) {
      await apiClient.put(`/api/v1/eventos-adversos/locais/${currentLocal.value.id}/`, {
        nome: currentLocal.value.nome,
        descricao: currentLocal.value.descricao,
        ativo: currentLocal.value.ativo
      });
    } else {
      await apiClient.post('/api/v1/eventos-adversos/locais/', {
        nome: currentLocal.value.nome,
        descricao: currentLocal.value.descricao,
        ativo: currentLocal.value.ativo
      });
    }
    await loadLocais();
    closeModal();
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.response?.data?.detail || 'Erro ao salvar local.';
    error.value = errorMessage;
    console.error('Erro ao salvar local:', err);
  }
}

function editLocal(local) {
  openModal(local);
}

async function deleteLocal(local) {
  if (!confirm(`Tem certeza que deseja excluir o local "${local.nome}"?`)) {
    return;
  }

  try {
    error.value = null;
    await apiClient.delete(`/api/v1/eventos-adversos/locais/${local.id}/`);
    await loadLocais();
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.response?.data?.detail || 'Erro ao excluir local.';
    error.value = errorMessage;
    alert(errorMessage);
    console.error('Erro ao excluir local:', err);
  }
}

onMounted(() => {
  // Verificar permissões antes de carregar dados
  if (!temPermissao.value) {
    return;
  }
  loadLocais();
});
</script>
